import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, Package, MapPin } from 'lucide-react';

export const Account = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    api.get('/auth/me').then(res => {
      setProfile({
        name: res.data.name || '',
        phone: res.data.phone || '',
        address: res.data.address || '',
        city: res.data.city || '',
        state: res.data.state || '',
        pincode: res.data.pincode || ''
      });
    });
  }, [user, navigate]);

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/auth/profile', profile);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-orange-800 mb-8">My Account</h1>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Sidebar */}
        <div className="space-y-2">
          <button className="w-full flex items-center gap-3 p-3 rounded-lg bg-orange-100 text-orange-700">
            <User size={20} /> Profile
          </button>
          <button
            onClick={() => navigate('/orders')}
            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100"
          >
            <Package size={20} /> My Orders
          </button>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 p-3 rounded-lg text-red-600 hover:bg-red-50"
          >
            Logout
          </button>
        </div>

        {/* Profile Form */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <User size={20} /> Profile Information
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input name="name" value={profile.name} onChange={handleChange} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input name="phone" value={profile.phone} onChange={handleChange} />
                </div>
              </div>

              <div>
                <Label>Email</Label>
                <Input value={user.email} disabled className="bg-gray-50" />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>

              <h3 className="text-md font-semibold pt-4 flex items-center gap-2">
                <MapPin size={18} /> Default Shipping Address
              </h3>

              <div>
                <Label>Address</Label>
                <Input
                  name="address"
                  value={profile.address}
                  onChange={handleChange}
                  placeholder="House/Flat No., Street, Landmark"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>City</Label>
                  <Input name="city" value={profile.city} onChange={handleChange} />
                </div>
                <div>
                  <Label>State</Label>
                  <Input name="state" value={profile.state} onChange={handleChange} />
                </div>
                <div>
                  <Label>Pincode</Label>
                  <Input name="pincode" value={profile.pincode} onChange={handleChange} />
                </div>
              </div>

              <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700">
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
